import React from "react";

import { AVATAR_LIST } from "enefel/avatars";
import { AVATAR_DEFAULT_LIST } from "enefel/avatarsDefault";
import { findSeasonAvatar, getSeasonAvatarPath } from "enefel/avatarsSeason";
import publicPath from "../../../helpers/publicPath";

import AvatarImg from "./AvatarImg";

type AvatarShowProps = {
  player: any;
  avatarFile: string;
  isUsed?: boolean;
  withTooltip?: boolean;
  /** Numéro de saison à utiliser si findSeasonAvatar échoue (fallback) */
  season?: number;
};

const AvatarShow: React.FC<AvatarShowProps> = React.memo(
  ({ player, avatarFile, isUsed = false, withTooltip = true, season }) => {
    if (!avatarFile) {
      return null;
    }

    // Nouveau format "s17/1.png" : saison encodée dans l'itemKey
    if (avatarFile.includes("/")) {
      const slashIdx = avatarFile.indexOf("/");
      const s = parseInt(avatarFile.slice(1, slashIdx), 10);
      // Normalise .jpeg → .png (anciens enregistrements DB peuvent avoir .jpeg)
      const file = avatarFile.slice(slashIdx + 1).replace(".jpeg", ".png");
      const meta = findSeasonAvatar(file);
      const src = publicPath(`${getSeasonAvatarPath(s)}/${file}`);
      return (
        <AvatarImg
          alt={player.name}
          src={src}
          isUsed={isUsed}
          withTooltip={withTooltip}
          rarity={meta?.rarity}
          portrait={true}
        />
      );
    }

    // Avatar saisonnier — chemin direct sans sous-dossier de type
    const seasonAvatar = findSeasonAvatar(avatarFile);
    if (seasonAvatar) {
      const avatar = publicPath(`${getSeasonAvatarPath(seasonAvatar.season)}/${avatarFile}`);
      return (
        <AvatarImg
          alt={player.name}
          src={avatar}
          isUsed={isUsed}
          withTooltip={withTooltip}
          rarity={seasonAvatar.rarity}
          portrait={seasonAvatar.portrait}
        />
      );
    }

    // Fallback : avatar saisonnier acheté mais absent de la liste courante
    if (season !== undefined) {
      const avatar = publicPath(`${getSeasonAvatarPath(season)}/${avatarFile}`);
      return (
        <AvatarImg
          alt={player.name}
          src={avatar}
          isUsed={isUsed}
          withTooltip={withTooltip}
          portrait={true}
        />
      );
    }

    // Avatar standard - recherche dans tous les types (pas uniquement la carrière du joueur)
    let avatarType: string | null = null;
    let path = "tile/shop/avatar";
    let rarity: string | undefined;

    for (const [type, avatars] of Object.entries(AVATAR_LIST)) {
      const found = (avatars as any[]).find((a) => a.file === avatarFile);
      if (found) {
        avatarType = type;
        path = "tile/shop/avatar";
        rarity = found.rarity;
        break;
      }
    }
    if (!avatarType) {
      for (const [type, avatars] of Object.entries(AVATAR_DEFAULT_LIST)) {
        const found = (avatars as any[]).find((a) => a.file === avatarFile);
        if (found) {
          avatarType = type;
          path = "tile/shop/avatar-default";
          rarity = found.rarity;
          break;
        }
      }
    }
    if (!avatarType) {
      return null;
    }
    const avatar = publicPath(`${path}/${avatarType}/${avatarFile}`);

    return (
      <AvatarImg
        alt={player.name}
        src={avatar}
        isUsed={isUsed}
        withTooltip={withTooltip}
        rarity={rarity}
      />
    );
  }
);

export default AvatarShow;
